import { useState } from 'react';
import { Button, Stack } from '@mui/material';
import ConfirmationDialog from './ConfirmationDialog';
import { canRunAction } from '../utils/scenarioState';

const ACTION_LABEL = {
  solve: 'Solve',
  approve: 'Approve',
  publish: 'Publish',
  reconcile: 'Crosscheck actual',
  reoptimize: 'Create revision',
  cancel: 'Cancel scenario',
};

const ACTIONS_REQUIRING_CONFIRM = new Set(['publish', 'cancel']);

const CONFIRM_COPY = {
  publish: {
    title: 'Publish this scenario?',
    description: 'Publishing supersedes the current published plan. Preserved/running work carried over from it will keep its identity; other plans that are not part of this revision chain will no longer be current.',
    confirmColor: 'primary',
  },
  cancel: {
    title: 'Cancel this scenario?',
    description: 'This scenario will no longer be actionable. This does not affect any other published plan.',
    confirmColor: 'error',
  },
};

// Renders solve/approve/publish/reconcile/cancel buttons gated by the scenario's *current*
// server status (never guessed from local state), with a confirmation step for irreversible
// actions like publish/cancel. onAction receives the action name and must call the API + refresh.
export default function LifecycleActions({ status, busyAction, onAction, disabledReason }) {
  const [pendingAction, setPendingAction] = useState(null);

  const trigger = (action) => {
    if (ACTIONS_REQUIRING_CONFIRM.has(action)) {
      setPendingAction(action);
    } else {
      onAction(action);
    }
  };

  const confirmCopy = pendingAction ? CONFIRM_COPY[pendingAction] : null;

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {Object.keys(ACTION_LABEL).map((action) => {
          const eligible = canRunAction(status, action);
          if (!eligible && busyAction !== action) return null;
          return (
            <Button
              key={action}
              variant={action === 'publish' ? 'contained' : 'outlined'}
              color={action === 'cancel' ? 'error' : 'primary'}
              disabled={!eligible || Boolean(busyAction)}
              onClick={() => trigger(action)}
            >
              {busyAction === action ? 'Working…' : ACTION_LABEL[action]}
            </Button>
          );
        })}
      </Stack>
      {disabledReason && (
        <Stack direction="row" sx={{ mt: 1 }}>
          <small style={{ color: 'var(--mui-palette-text-secondary, #6b7280)' }}>{disabledReason}</small>
        </Stack>
      )}
      {confirmCopy && (
        <ConfirmationDialog
          open
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmColor={confirmCopy.confirmColor}
          confirmLabel={ACTION_LABEL[pendingAction]}
          busy={busyAction === pendingAction}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => {
            onAction(pendingAction);
            setPendingAction(null);
          }}
        />
      )}
    </>
  );
}
